import { Box, Flex, HStack, Skeleton, useColorModeValue } from "@chakra-ui/react";
import CDS from "design-system";
import { useNavigate } from "react-router";
import PathMenuButton from "features/Path/Components/Button/PathMenuButton";
import { Path, PathDetails } from "features/Path/models";
import { v2pathPathById } from "helpers/url/pages/v2/pathPath";
import AssignmentPill from "./AssignmentPill";
import IconTile from "./IconTile";
import LastUsed from "./LastUsed";

interface PathRowProps {
  path: Path;
  details?: PathDetails;
  isDetailsLoading?: boolean;
}

export default function PathRow({ path, details, isDetailsLoading = false }: PathRowProps) {
  const navigate = useNavigate();
  const hoverBg = useColorModeValue("gray.50", "whiteAlpha.100");
  const borderColor = useColorModeValue("gray.100", "whiteAlpha.200");

  const stepsCount = details?.stepsCount;
  const lastUsedAt = details?.lastUsedAt ?? null;

  return (
    <Flex
      role="button"
      tabIndex={0}
      onClick={() => navigate(v2pathPathById(path.id))}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(v2pathPathById(path.id));
        }
      }}
      align="center"
      gap="4"
      py="3"
      px="3"
      borderBottomWidth="1px"
      borderColor={borderColor}
      cursor="pointer"
      _hover={{ bg: hoverBg }}
      transition="background-color 120ms"
    >
      <IconTile icon={path.icon} color={path.iconColor} />

      <Box flex="1" minW={0}>
        <CDS.Text14 fontWeight="500" color="gray.800" noOfLines={1}>
          {path.name}
        </CDS.Text14>
        {isDetailsLoading ? (
          <Skeleton height="14px" width="60px" mt="1" />
        ) : (
          <CDS.Text12 color="gray.500" mt="0.5">
            {stepsCount ?? 0}
            {" "}
            {stepsCount === 1 ? "step" : "steps"}
          </CDS.Text12>
        )}
      </Box>

      <Box flexShrink={0}>
        <AssignmentPill autoAdd={path.autoAdd} />
      </Box>

      <Box w="100px" textAlign="right" flexShrink={0}>
        {isDetailsLoading ? (
          <Skeleton height="14px" width="80px" ml="auto" />
        ) : (
          <LastUsed lastUsedAt={lastUsedAt} />
        )}
      </Box>

      <HStack
        spacing={0}
        flexShrink={0}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <PathMenuButton path={path} />
      </HStack>
    </Flex>
  );
}
